// FriendsPage.js
import React, { useContext } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Icon } from '@iconify/react';
import { useUserData } from '../contexts/UserDataContext';
import { TelegramUserContext } from '../App';
import { buildReferralUrl, APP_CONFIG } from '../config/app';

// ----------------------------
// CONFIGURATION
// ----------------------------

const SOCIAL_ICONS = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'ic:outline-whatsapp',
    bg: '#25D366'
  },
  {
    id: 'messenger',
    name: 'Messenger',
    icon: 'ri:messenger-fill',
    bg: 'linear-gradient(135deg, #00C6FF 0%, #7D5FFF 50%, #FF2D95 100%)'
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: 'skill-icons:discord',
    bg: '#5865F2'
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    icon: 'prime:twitter',
    bg: '#000000'
  },
  {
    id: 'link',
    name: 'Link',
    icon: 'fluent:link-24-regular',
    bg: '#7B7B7B'
  }
];

const INVITE_ITEMS = [
  {
    id: 'telegram',
    title: 'Invite friends',
    reward: '+1000 $PXL',
    icon: 'logos:telegram',
    type: 'icon'
  },
  {
    id: 'premium',
    title: 'Invite premium friends',
    reward: '+2000 $PXL',
    icon: 'material-symbols:star-rate',
    bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6B6B 100%)',
    type: 'icon'
  }
];

// ----------------------------
// STYLES (MUST BE DEFINED BEFORE COMPONENT)
// ----------------------------

const Container = styled.div`
  font-size: 13px;
  background: #ffffff;
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  position: relative;
  margin: 0;
  padding: 0;
  padding-top: 70px; /* Account for fixed header */
  padding-bottom: 80px; /* Account for fixed navigation */

  @media (max-width: 375px) {
    font-size: 12px;
    padding-top: 60px;
    padding-bottom: 70px;
  }
`;

const HeaderWrapper = styled.div`
  /* Header is now fixed, no need for wrapper positioning */
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 90px; /* Increased padding to ensure content is fully scrollable */
  overscroll-behavior: none; /* Prevent pull-to-refresh and overscroll */
  -webkit-overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y; /* Allow vertical scrolling but prevent pull gestures */
  width: 100%;
  height: calc(100vh - 60px); /* Use viewport height minus header */

  @media (max-width: 375px) {
    padding-bottom: 85px;
    height: calc(100vh - 55px);
  }
`;

const FooterWrapper = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  background: white;
  z-index: 100;
  flex-shrink: 0;
`;

const FriendsContainer = styled.div`
  margin: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const InviteItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const InviteTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const RewardText = styled.div`
  font-size: 14px;
  color: #666;
`;

const ShareButton = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 0;
  width: 80%;
  margin: 16px auto;
  font-weight: bold;
  cursor: pointer;
  display: block;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 16px;
  padding: 0 4px;
  flex-wrap: nowrap;
  overflow-x: auto;
  
  /* Hide scrollbar but keep functionality */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SocialIcon = styled.button`
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.2s ease, opacity 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }

  &:hover {
    opacity: 0.8;
    transform: scale(0.95);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const AffiliateSection = styled.div`
  background: white;
  margin: 16px;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const AffiliateTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 8px;
`;

const AffiliateDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
`;

const AmountDisplay = styled.div`
  font-size: 40px;
  font-weight: bold;
  margin: 16px 0;
`;

const WithdrawButton = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 12px 0;
  width: 80%;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
`;

const CommissionLink = styled.a`
  color: #666;
  font-size: 14px;
  text-decoration: underline;
  display: block;
  margin-top: 16px;
`;

// ----------------------------
// COMPONENT
// ----------------------------

const FriendsPage = ({ onNavigate }) => {
  const { userData } = useUserData();
  const telegramUser = useContext(TelegramUserContext);

  // Handle social sharing
  const handleSocialShare = (platform) => {
    const referralCode = userData?.referral_code || telegramUser?.id;
    if (!referralCode) return;

    const referralUrl = buildReferralUrl(referralCode);
    const shareText = `Join me in ${APP_CONFIG.APP_NAME} and earn ${APP_CONFIG.REFERRAL_REWARD} ${APP_CONFIG.CURRENCY_SYMBOL}!`;

    switch (platform) {
      case 'whatsapp':
        window.location.href = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + referralUrl)}`;
        break;
      case 'messenger':
        window.location.href = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(referralUrl)}&app_id=your_app_id&redirect_uri=${encodeURIComponent(window.location.href)}`;
        break;
      case 'discord':
        const discordMessage = `${shareText} ${referralUrl}`;
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(discordMessage);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = discordMessage;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.showAlert('Message copied! Now open Discord and paste it in your server or DM.');
        } else {
          alert('Message copied! Now open Discord and paste it in your server or DM.');
        }
        break;
      case 'x':
        window.location.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralUrl)}`;
        break;
      case 'link':
        // Copy referral link to clipboard
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(referralUrl);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = referralUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.showAlert('Referral link copied to clipboard!');
        } else {
          alert('Referral link copied to clipboard!');
        }
        break;
      default:
        break;
    }
  };

  return (
    <Container>
      <HeaderWrapper>
        <Header dogeBalance={userData?.balance || 0} pxlBalance={userData?.pxl_balance || 0} />
      </HeaderWrapper>

    <ScrollableContent>
      <FriendsContainer>
        {INVITE_ITEMS.map(item => (
          <InviteItem key={item.id}>
            <IconBox style={{ background: item.bg || 'transparent' }}>
              <Icon icon={item.icon} color="#fff" width="100%" height="100%" />
            </IconBox>
            <TextContent>
              <InviteTitle>{item.title}</InviteTitle>
              <RewardText>{item.reward}</RewardText>
            </TextContent>
          </InviteItem>
        ))}

        <ShareButton>Share with friends</ShareButton>

        <SocialIcons>
          {SOCIAL_ICONS.map(({ id, name, icon, bg, size }) => (
            <SocialIcon 
              key={id} 
              title={name} 
              style={{ background: bg }}
              onClick={() => handleSocialShare(id)}
            >
              <Icon icon={icon} width={size || '24px'} height={size || '24px'} />
            </SocialIcon>
          ))}
        </SocialIcons>
      </FriendsContainer>

      <AffiliateSection>
        <AffiliateTitle>Affiliate programs</AffiliateTitle>
        <AffiliateDescription>
          Invite friends to join the mining app and earn rewards.
        </AffiliateDescription>
        <AmountDisplay>$0</AmountDisplay>
        <WithdrawButton>Withdrawal</WithdrawButton>
        <CommissionLink href="#">Commission details</CommissionLink>
      </AffiliateSection>
    </ScrollableContent>

    <FooterWrapper>
      <Navigation activePage="friends" onNavigate={onNavigate} />
    </FooterWrapper>
  </Container>
  );
};

export default FriendsPage;
